import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Grid,
  Snackbar,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleIcon from '@mui/icons-material/People';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MessageIcon from '@mui/icons-material/Message';
import ForumIcon from '@mui/icons-material/Forum';
import ReplyIcon from '@mui/icons-material/Reply';
import { Link as RouterLink } from 'react-router-dom';
import { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import DataTable from '../components/DataTable';
import StatCard from '../components/StatCard';
import { useAdminApi } from '../hooks/useAdminApi';
import { UserOverviewResponse, UserOverviewSummary } from '../types/adminApiTypes';
import { formatNumber, formatDate } from '../utils/helpers';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';

const USER_TYPE_OPTIONS = [
  { label: 'All Types', value: '' },
  { label: 'Admin', value: 'admin' },
  { label: 'Internal', value: 'internal' },
  { label: 'External Tester', value: 'ext_tester' },
  { label: 'Standard', value: 'standard' },
  { label: 'API', value: 'api' },
];

const UsersListView: React.FC = () => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'email', sort: 'asc' }]);
  // inputValue: what the user is currently typing; submittedSearch: what was last submitted via Enter
  const [inputValue, setInputValue] = useState<string>('');
  const [submittedSearch, setSubmittedSearch] = useState<string>('');
  const isPendingSearch = inputValue !== submittedSearch;
  const [selectedUserTypes, setSelectedUserTypes] = useState<string[]>([]);
  const [typeFilterAnchor, setTypeFilterAnchor] = useState<HTMLElement | null>(null);

  const { user } = useSelector((state: RootState) => state.user);
  const isAdmin = user?.userType === 'admin';

  const cursorCacheRef = useRef<Record<number, string>>({});
  const lastFetchedPageRef = useRef<number>(0);

  const { data, loading, error, fetchData } = useAdminApi<UserOverviewResponse>();

  // Keep a ref to fetchData so auth token refreshes inside useAdminApi don't
  // change the function reference and trigger unintended re-fetches.
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  // Keep rowCount stable while loading so MUI DataGrid doesn't auto-reset the
  // page to 0 when data becomes null mid-fetch (which would trigger a second
  // no-cursor fetch, corrupting cursor-based pagination).
  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    setRowCountState(prev => (data?.total_users !== undefined ? data.total_users : prev));
  }, [data?.total_users]);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleTypeFilterClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setTypeFilterAnchor(event.currentTarget);
  }, []);

  const handleTypeFilterClose = useCallback(() => {
    setTypeFilterAnchor(null);
  }, []);

  const handleTypeSelect = useCallback((value: string) => {
    if (value === '') {
      setSelectedUserTypes([]);
    } else {
      setSelectedUserTypes(prev =>
        prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]
      );
    }
  }, []);

  const columns = useMemo(
    (): GridColDef<UserOverviewSummary>[] => [
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.5,
        renderCell: params => (
          <MuiLink component={RouterLink} to={`/dashboard/users/${params.row.user_id}`}>
            {params.value}
          </MuiLink>
        ),
      },
      {
        field: 'organization',
        headerName: 'Organization',
        flex: 1,
        valueGetter: value => value || 'N/A',
      },
      {
        field: 'user_type',
        headerName: 'Type',
        width: 100,
        sortable: false,
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ flexGrow: 1, fontWeight: 500, fontSize: '0.875rem' }}>Type</Box>
            <Tooltip
              title={
                selectedUserTypes.length > 0
                  ? `Filtered: ${selectedUserTypes.join(', ')}`
                  : 'Filter by type'
              }
            >
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  handleTypeFilterClick(e);
                }}
                sx={{
                  p: 0.25,
                  color: selectedUserTypes.length > 0 ? 'primary.main' : 'action.active',
                }}
                aria-label="Filter by type"
              >
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'created_at',
        headerName: 'Created At',
        width: 140,
        valueFormatter: value => formatDate(value),
      },
      {
        field: 'last_activity',
        headerName: 'Last Activity',
        width: 140,
        valueFormatter: value => (value ? formatDate(value, 'D MMM YYYY HH:mm') : 'N/A'),
      },
      {
        field: 'total_user_messages',
        headerName: 'Messages',
        type: 'number',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: value => formatNumber(value),
      },
      {
        field: 'total_chats',
        headerName: 'Chats',
        type: 'number',
        width: 100,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: value => formatNumber(value),
      },
      {
        field: 'total_follow_ups',
        headerName: 'Follow-ups',
        type: 'number',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: value => formatNumber(value),
      },
      {
        field: 'total_costs',
        headerName: 'Costs',
        type: 'number',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: value => {
          const numValue = value as number;
          if (numValue === null || numValue === undefined) return '$0.00';
          return `$${numValue.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
      },
    ],
    [selectedUserTypes, handleTypeFilterClick]
  );

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      cursorCacheRef.current = {};
      setPaginationModel({ page: 0, pageSize: newModel.pageSize });
    } else if (newModel.page !== paginationModel.page) {
      setPaginationModel(newModel);
    }
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    cursorCacheRef.current = {};
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setSortModel(newSortModel);
  };

  // Reset cursor cache and go to page 0 whenever the committed search term or type filter changes.
  useEffect(() => {
    cursorCacheRef.current = {};
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [submittedSearch, selectedUserTypes]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setSubmittedSearch(inputValue);
    }
  };

  useEffect(() => {
    lastFetchedPageRef.current = paginationModel.page;

    const params: Record<string, any> = {
      endpoint: '/users/overview',
      page_size: paginationModel.pageSize,
    };

    const cursor =
      paginationModel.page > 0 ? cursorCacheRef.current[paginationModel.page] : undefined;
    if (cursor) params.cursor = cursor;

    if (sortModel.length > 0) {
      params.sort_by = sortModel[0].field;
      if (sortModel[0].sort) params.sort_order = sortModel[0].sort;
    }

    if (submittedSearch) {
      params.search_term = submittedSearch;
    }

    if (selectedUserTypes.length > 0) {
      params.user_type = selectedUserTypes;
    }

    fetchDataRef.current(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    submittedSearch,
    selectedUserTypes,
  ]);

  useEffect(() => {
    if (data?.next_cursor) {
      cursorCacheRef.current[lastFetchedPageRef.current + 1] = data.next_cursor;
    }
  }, [data]);

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" component="div">
          Users Overview
        </Typography>
        {isAdmin && (
          <Button
            component={RouterLink}
            to="/dashboard/users/create"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create New User
          </Button>
        )}
      </Box>

      {/* Summary Tiles Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Users"
            value={data?.summary?.total_users}
            icon={<PeopleIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Users"
            value={data?.summary?.active_users}
            icon={<RecentActorsIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Costs"
            value={data?.summary?.total_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total User Messages"
            value={data?.summary?.total_user_messages}
            icon={<MessageIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Chats"
            value={data?.summary?.total_chats}
            icon={<ForumIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Follow-ups"
            value={data?.summary?.total_follow_ups}
            icon={<ReplyIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Users List Section */}
      <Box sx={{ mt: 4 }}>
        {/* Search Bar */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by email…"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={{ width: { xs: '100%', sm: '300px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: isPendingSearch ? (
                <InputAdornment position="end">
                  <Tooltip title="Press Enter to search">
                    <KeyboardReturnIcon fontSize="small" color="action" />
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        <DataTable
          rows={data?.users || []}
          columns={columns}
          loading={loading}
          getRowId={row => row.user_id}
          rowCount={rowCountState}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          paginationMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingMode="server"
        />
      </Box>

      {/* Type Filter Menu */}
      <Menu
        anchorEl={typeFilterAnchor}
        open={Boolean(typeFilterAnchor)}
        onClose={handleTypeFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ elevation: 2, sx: { minWidth: 140 } }}
      >
        {USER_TYPE_OPTIONS.map(({ label, value }) => (
          <MenuItem key={value || 'all'} onClick={() => handleTypeSelect(value)} dense>
            <Checkbox
              size="small"
              checked={
                value === '' ? selectedUserTypes.length === 0 : selectedUserTypes.includes(value)
              }
              sx={{ p: 0.5, mr: 0.5 }}
            />
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        ContentProps={{
          sx: {
            backgroundColor: snackbar.severity === 'success' ? 'success.main' : 'error.main',
          },
        }}
      />
    </Box>
  );
};

export default UsersListView;
